import ExcelJS from 'exceljs';

function forceCollapse(row) {
    Object.defineProperty(row, 'collapsed', {
        value: true,
        writable: true,
        enumerable: true,
        configurable: true
    });
    if (row._model) {
        row._model.collapsed = true;
    }
}

export const ExcelExporter = {
    /**
     * Export a generic financial report
     * @param {Object} options
     * @param {string} options.title - Report Title
     * @param {string} options.subtitle - Report Subtitle (e.g. Month/Year)
     * @param {Array} options.columns - Column definitions [{ header: 'Name', key: 'name', width: 20, style: {} }]
     * @param {Array} options.data - Data rows (hierarchical or flat)
     * @param {string} options.fileName - Output file name
     */
    async exportReport({ title, subtitle, columns, data, fileName }) {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Report');
        worksheet.properties.outlineProperties = { summaryBelow: false };

        // 1. Title & Subtitle
        worksheet.mergeCells('A1', 'E1'); // Adjust merge based on column count?
        const titleCell = worksheet.getCell('A1');
        titleCell.value = title.toUpperCase();
        titleCell.font = { name: 'Times New Roman', size: 16, bold: true };
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };

        if (subtitle) {
            worksheet.mergeCells('A2', 'E2');
            const subTitleCell = worksheet.getCell('A2');
            subTitleCell.value = subtitle;
            subTitleCell.font = { name: 'Times New Roman', size: 12, italic: true };
            subTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        }

        // 2. Headers
        const headerRow = worksheet.getRow(4);
        columns.forEach((col, index) => {
            const cell = headerRow.getCell(index + 1);
            cell.value = col.header;
            cell.font = { name: 'Times New Roman', size: 11, bold: true };
            cell.alignment = { vertical: 'middle', horizontal: col.align || 'center' };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF5F5F5' } // Light gray
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
            worksheet.getColumn(index + 1).width = col.width || 15;
        });

        // 3. Data Rows
        const rowObjects = [];
        let currentRowIdx = 5;

        // Populate rows and set properties
        data.forEach(item => {
            const row = worksheet.getRow(currentRowIdx);

            columns.forEach((col, colIndex) => {
                const cell = row.getCell(colIndex + 1);
                let value = item[col.key];

                // Visual Level (for spaces and hyphens)
                const visualLevel = (item.style && item.style.indent) || 0;

                // Visual Indentation (Prefix spaces and symbols)
                if (colIndex === 0) {
                    if (visualLevel > 0 && typeof value === 'string') {
                        // User's prefix preference
                        value = '    '.repeat(visualLevel) + '- ' + value;
                    }
                }

                // Formatting
                if (col.format === 'currency') {
                    // Custom accounting format: Negative in parens, no red
                    cell.numFmt = '#,##0_);(#,##0)';
                }

                cell.value = value;
                cell.font = { name: 'Times New Roman', size: 11 };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };

                // Custom Styles from Item data
                if (item.style) {
                    if (item.style.bold) cell.font.bold = true;
                    if (item.style.italic) cell.font.italic = true;
                    if (item.style.background) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: item.style.background }
                        };
                    }
                    if (item.style.indent && colIndex === 0) {
                        cell.alignment = { indent: item.style.indent };
                    }
                }

                if (col.align) {
                    cell.alignment = { ...cell.alignment, horizontal: col.align };
                }
            });

            // Outline Level / Grouping basics
            // Use item.outlineLevel if provided, otherwise default to indent level
            // If item.outlineLevel is specifically 0, it will not be collapsible.
            let outlineLevel = (item.outlineLevel !== undefined) ? item.outlineLevel : ((item.style && item.style.indent) || 0);

            if (outlineLevel !== undefined) {
                row.outlineLevel = outlineLevel;

                // Collapse logic: 
                // To show '+' on parent, the children must be both 'hidden' and 'collapsed'.
                if (outlineLevel > 0 && !(item.style && item.style.expanded)) {
                    // 1. Hide the row
                    row.hidden = true;
                    // 2. Mark as collapsed so parent shows '+'
                    if (row.model) {
                        row.model.collapsed = true;
                    } else {
                        row._collapsed = true;
                    }
                } else {
                    row.hidden = false;
                }

                // Force collapse for outlineLevel 0
                if (outlineLevel === 0) {
                    forceCollapse(row);
                }
            }

            // Forced visibility/hidden metadata
            if (item.style && item.style.hidden) {
                row.hidden = true;
            }

            rowObjects.push({ row, item });
            currentRowIdx++;
        });

        // 4. Save
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${fileName}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};
